import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { buildLoginUrl } from '../../utils/navigation';
import toast from 'react-hot-toast';

// Module-level flag: ensures only ONE "session expired" toast + redirect fires
// even when many concurrent requests all return 401 at the same time.
let sessionExpiredFired = false;

export const AuthInterceptor: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Prevent infinite redirect loops if we're already on auth pages
          const isAuthPage = /^\/(signin|signup)/.test(location.pathname);

          if (!isAuthPage && !sessionExpiredFired) {
            sessionExpiredFired = true;

            signOut();
            toast.error('Your session has expired. Please sign in again.');

            // Build the return URL so the user comes back here after signing in
            const currentUrl = location.pathname + location.search;
            navigate(buildLoginUrl(currentUrl));

            // Reset the flag after navigation so future logins work normally
            setTimeout(() => { sessionExpiredFired = false; }, 3000);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [navigate, location, signOut]);

  return <>{children}</>;
};
